import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { EnvelopeSimple, PaperPlaneTilt, Gear, CheckCircle, ShieldCheck } from "@phosphor-icons/react";

// Email channel card with live sender-mode display (MODE 1 managed / MODE 2 tenant BYO Resend),
// a Send Test Email action, and a Manage Connection dialog. Credentials are write-only:
// the API key is sent to the server, never read back.
export function EmailChannelCard() {
  const [cfg, setCfg] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(() => {
    api.get("/connect/email/config").then((r) => setCfg(r.data)).catch(() => setCfg(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/connect/email/test", {});
      toast.success(`Test email sent (${data.mode === "byo" ? "your Resend account" : "RecoverOS Managed"}) to ${data.to}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the test email.");
    } finally { setTesting(false); }
  };

  const byo = cfg?.byo_active;

  return (
    <div className="border border-border bg-card p-4 rounded-sm flex flex-col h-full" data-testid="connect-channel-email">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <span className="h-9 w-9 rounded-sm bg-secondary flex items-center justify-center shrink-0">
            <EnvelopeSimple size={20} weight="bold" className="text-violet-600" />
          </span>
          <div className="font-heading font-bold">Email</div>
        </div>
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 shrink-0" data-testid="connect-channel-email-status">
          <span className="h-2 w-2 rounded-full bg-emerald-500" /> Connected
        </span>
      </div>

      {cfg ? (
        <div className="mt-3 space-y-1 text-xs" data-testid="connect-email-mode">
          <div className="flex flex-wrap gap-x-4 gap-y-0.5">
            <span className="text-muted-foreground">Mode: <span className="font-medium text-foreground" data-testid="connect-email-mode-label">{cfg.mode_label || (byo ? "Own Provider" : "RecoverOS Managed")}</span></span>
            {byo && cfg.provider_label && (
              <span className="text-muted-foreground">Provider: <span className="font-medium text-foreground" data-testid="connect-email-provider">{cfg.provider_label}</span></span>
            )}
          </div>
          <div className="text-muted-foreground pt-1">Sending as</div>
          <div className="font-medium break-words" data-testid="connect-email-sending-as">{cfg.sending_as}</div>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 pt-1 text-muted-foreground">
            <span>Reply-To: <span className="font-medium text-foreground">{cfg.reply_to || "—"}</span></span>
          </div>
          <div className="pt-1">
            {byo ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700"><ShieldCheck size={13} weight="bold" /> Your domain ({cfg.verified_domain}) — Verified</span>
            ) : (
              <span className="text-[11px] text-muted-foreground">Sent via RecoverOS Managed infrastructure — no setup required.</span>
            )}
          </div>
        </div>
      ) : <div className="mt-3 text-xs text-muted-foreground">Loading…</div>}

      <div className="mt-auto pt-3 flex gap-2">
        <Button variant="outline" size="sm" className="rounded-sm flex-1" onClick={sendTest} disabled={testing} data-testid="connect-email-test">
          <PaperPlaneTilt size={14} weight="bold" className="mr-1" /> {testing ? "Sending…" : "Send Test"}
        </Button>
        <Button variant="outline" size="sm" className="rounded-sm flex-1" onClick={() => setManageOpen(true)} data-testid="connect-email-manage">
          <Gear size={14} weight="bold" className="mr-1" /> Manage
        </Button>
      </div>

      <ManageDialog open={manageOpen} onOpenChange={setManageOpen} cfg={cfg} onChanged={load} />
    </div>
  );
}

function ManageDialog({ open, onOpenChange, cfg, onChanged }) {
  const [mode, setMode] = useState("managed");
  const [form, setForm] = useState({ api_key: "", sender_email: "", reply_to_email: "", business_name: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setMode(cfg?.byo_active ? "byo" : "managed"); }, [open, cfg]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const connect = async () => {
    if (!form.api_key || !form.sender_email) return toast.error("Resend API key and sender email are required.");
    setBusy(true);
    try {
      const { data } = await api.post("/connect/email/connect", form);
      toast.success(`Connected. Sending as ${data.sending_as} (domain ${data.verified_domain} verified).`);
      onChanged?.(); onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not connect your email provider.");
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post("/connect/email/disconnect", {});
      toast.success("Reverted to RecoverOS Managed Email.");
      onChanged?.(); onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not disconnect.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto" data-testid="connect-email-manage-dialog">
        <DialogHeader>
          <DialogTitle>Manage Email Connection</DialogTitle>
          <DialogDescription>Choose how RecoverOS sends your customer emails.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => setMode("managed")} data-testid="email-mode-managed"
            className={`text-left p-3 rounded-sm border text-xs ${mode === "managed" ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/60"}`}>
            <div className="font-semibold flex items-center gap-1.5">{mode === "managed" && <CheckCircle size={14} weight="fill" className="text-primary" />} RecoverOS Managed</div>
            <div className="text-muted-foreground mt-1">Send instantly. From "{"{Your Business}"} via RecoverOS", replies to your inbox.</div>
          </button>
          <button onClick={() => setMode("byo")} data-testid="email-mode-byo"
            className={`text-left p-3 rounded-sm border text-xs ${mode === "byo" ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/60"}`}>
            <div className="font-semibold flex items-center gap-1.5">{mode === "byo" && <CheckCircle size={14} weight="fill" className="text-primary" />} Connect Your Own</div>
            <div className="text-muted-foreground mt-1">Send from your own verified domain via your Resend account.</div>
          </button>
        </div>

        {mode === "byo" ? (
          <div className="space-y-3 mt-1">
            <div>
              <Label className="text-xs">Resend API Key</Label>
              <Input type="password" value={form.api_key} onChange={(e) => set("api_key", e.target.value)}
                placeholder="re_xxxxxxxxxxxx" className="rounded-sm mt-1" data-testid="byo-api-key" autoComplete="off" />
              <p className="text-[11px] text-muted-foreground mt-1">Stored encrypted. Never shown again after saving.</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Sender Email</Label>
                <Input value={form.sender_email} onChange={(e) => set("sender_email", e.target.value)}
                  placeholder="info@yourdomain.in" className="rounded-sm mt-1" data-testid="byo-sender-email" />
              </div>
              <div>
                <Label className="text-xs">Reply-To (optional)</Label>
                <Input value={form.reply_to_email} onChange={(e) => set("reply_to_email", e.target.value)}
                  placeholder="info@yourdomain.in" className="rounded-sm mt-1" data-testid="byo-reply-to" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Business Name (From name)</Label>
              <Input value={form.business_name} onChange={(e) => set("business_name", e.target.value)}
                placeholder="TinyLoan" className="rounded-sm mt-1" data-testid="byo-business-name" />
            </div>
            <p className="text-[11px] text-muted-foreground">The sender email's domain must be a <strong>verified</strong> sending domain in your Resend account.</p>
          </div>
        ) : (
          <div className="rounded-sm border border-border bg-secondary/30 p-3 text-xs text-muted-foreground mt-1">
            RecoverOS sends from its verified infrastructure as <strong>“{"{Your Business}"} via RecoverOS”</strong> with replies routed to your business email. No setup required.
          </div>
        )}

        <DialogFooter className="mt-2 gap-2">
          {cfg?.byo_active && (
            <Button variant="outline" className="rounded-sm mr-auto text-red-600" onClick={disconnect} disabled={busy} data-testid="byo-disconnect">
              Disconnect
            </Button>
          )}
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="manage-cancel">Cancel</Button>
          {mode === "byo" && (
            <Button className="rounded-sm" onClick={connect} disabled={busy} data-testid="byo-connect">
              {busy ? "Verifying…" : "Verify & Connect"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
